import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext.js";
import { api, ApiError } from "../../../api/client.js";
import { ReelApproved } from "./ReelApproved.js";
import { ReelTop20 } from "./ReelTop20.js";
import { ReelUsScoreBar, sortReelsByUsScore, useReelUsScores } from "./ReelUsScore.js";
import { OPERATOR_ACTION_ROLE, effectiveImageRole, roleAtLeast } from "../image/image-ui.js";

/** One entry of `GET /research/:tenantId/personas` (personas the caller may see). */
export interface ResearchPersonaOption {
  readonly personaId: string;
  readonly displayName: string;
}
/** Stage 3: one Research Run (manual) and what it collected. Values the provider did not supply are null and shown as "—", never as 0. */
export interface ResearchRunView {
  readonly id: string;
  readonly status: "running" | "completed" | "partial" | "failed";
  readonly statusDetail: string | null;
  readonly creatorsTotal: number;
  readonly startedAt: string;
  readonly finishedAt: string | null;
}
export interface ResearchRunCreatorView {
  readonly creatorId: string;
  readonly canonicalAccountId: string;
  readonly status: "pending" | "completed" | "partial" | "failed" | "not_tracked";
  readonly errorCode: string | null;
  readonly errorMessage: string | null;
  readonly reelsReceived: number;
  readonly reelsNew: number;
  readonly reelsSeenBefore: number;
  readonly duplicatesSkipped: number;
  readonly rejectedCount: number;
  readonly truncated: boolean;
}
/** Stage 5: the stored Performance Analysis of a Reel (relative performance = views / the creator's median views). */
export interface ResearchReelAnalysisView {
  readonly status: "analyzed" | "insufficient_data";
  readonly insufficientReason: string | null;
  readonly relativePerformance: number | null;
  readonly interesting: boolean;
  readonly maturity: "early" | "mature" | "unknown";
  readonly likeRate: number | null;
  readonly commentRate: number | null;
}
/** Stage 6: the stored AI content analysis of an interesting Reel. FACTS and AI INTERPRETATION are separate objects and are shown separately. */
export interface ContentAnalysisDetail {
  readonly status: "acquiring_media" | "media_ready" | "analyzing" | "completed" | "failed";
  readonly failedStage: "media" | "analysis" | null;
  readonly errorCode: string | null;
  readonly errorMessage: string | null;
  readonly facts: { readonly reel: { readonly caption: string | null; readonly uploadedAt: string | null; readonly songName: string | null; readonly creator: string }; readonly performance: { readonly views: number | null; readonly likes: number | null; readonly comments: number | null; readonly creatorMedianViews: number | null; readonly relativePerformance: number | null } } | null;
  readonly interpretation: {
    readonly summary: string;
    readonly structure: readonly { readonly atSeconds: number | null; readonly description: string }[];
    readonly hook: string | null;
    readonly notableElements: readonly string[];
    readonly likelyAttentionDrivers: readonly { readonly driver: string; readonly rationale: string }[];
    readonly limitations: readonly string[];
  } | null;
  readonly provider: string | null;
  readonly model: string | null;
}
export interface ResearchRunReelView {
  readonly reelId: string;
  readonly canonicalUrl: string;
  readonly canonicalAccountId: string;
  readonly observationCount: number;
  readonly observation: {
    readonly caption: string | null;
    readonly uploadedAt: string | null;
    readonly views: number | null;
    readonly likes: number | null;
    readonly comments: number | null;
    readonly shares: number | null;
    readonly bookmarks: number | null;
    readonly engagement: number | null;
  };
  readonly analysis: ResearchReelAnalysisView | null;
  readonly contentAnalysis: { readonly status: ContentAnalysisDetail["status"]; readonly failedStage: ContentAnalysisDetail["failedStage"] } | null;
}
interface RunResponse {
  readonly run: ResearchRunView;
  readonly creators: readonly ResearchRunCreatorView[];
}

type ResearchBankState = "not_set_up" | "ready" | "needs_attention";
/** Customer wording for the Research Bank errors - the server's own messages can name the provider and are never shown for these. */
const BANK_ERROR_MESSAGE: Record<string, string> = {
  research_bank_needs_attention: "This persona's Research Bank needs attention. Please contact support - nothing was created.",
  research_bank_in_progress: "The Research Bank is being set up. Try again in a moment.",
  research_provider_failed: "Could not prepare this for research. Nothing was saved. Please try again.",
  research_provider_not_configured: "Research is not available on this server.",
};
const RUN_ERROR_MESSAGE: Record<string, string> = {
  no_creators: "Research is not ready for this persona yet.",
  research_binding_missing: "Research is not set up for this persona yet.",
  research_selection_failed: "Research could not select Reels right now. Nothing was saved. Please try again.",
  research_provider_not_configured: "The research data provider is not configured on this server, so a Research Run cannot start.",
  run_in_progress: "This run is currently being collected.",
  run_completed: "This run is already completed.",
};

/**
 * REEL: the Research area of Create. Choose a persona, then Research Run -> Top 30 -> Approved Reels. The research accounts of a persona's bank are internal infrastructure (FIX-053):
 * this screen neither lists nor manages them, and the user never has to supply accounts. Loads nothing until this page is opened.
 */
/** The run's Reels are shown as the Top 30 only (a run collected under the current rule holds the strongest Reels of the last 7 days (14 before FIX-080 / t1557u)) (the API returns them highest Relative Performance first, so the first 30 ARE the Top 30). */
const TOP_REELS_LIMIT = 30;

export function ReelResearchTab({ tenantId }: { readonly tenantId: string }) {
  const { csrfToken, user, memberships } = useAuth();
  const canAct = roleAtLeast(effectiveImageRole({ isPlatformOwner: user?.isPlatformOwner === true, memberships, tenantId }), OPERATOR_ACTION_ROLE);
  const base = `/research/${encodeURIComponent(tenantId)}`;
  const [personas, setPersonas] = useState<readonly ResearchPersonaOption[] | null>(null);
  const [personaId, setPersonaId] = useState<string>("");
  /** FIX-053: the persona's Research Bank state (neutral; null until read). Drives the set-up / needs-attention notice only; a ready bank shows nothing here. */
  const [bank, setBank] = useState<ResearchBankState | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [runs, setRuns] = useState<readonly ResearchRunView[] | null>(null);
  const [activeRun, setActiveRun] = useState<RunResponse | null>(null);
  const [reels, setReels] = useState<readonly ResearchRunReelView[] | null>(null);
  const [runBusy, setRunBusy] = useState(false);
  const [approvedRefresh, setApprovedRefresh] = useState(0);
  // FIX-080 (owner t1552u): the US score of the active run's Top 30; the list below is ranked by it (unscored Reels last).
  const us = useReelUsScores(base, personaId, activeRun?.run.id ?? null, csrfToken);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ personas: readonly ResearchPersonaOption[] }>(`${base}/personas`)
      .then((r) => {
        if (!cancelled) setPersonas(r.personas);
      })
      .catch(() => {
        if (!cancelled) setError("Could not load personas.");
      });
    return () => {
      cancelled = true;
    };
  }, [base]);

  const loadRun = useCallback(
    async (pid: string, runId: string) => {
      try {
        const base2 = `${base}/personas/${encodeURIComponent(pid)}/runs/${encodeURIComponent(runId)}`;
        const [run, rr] = await Promise.all([api.get<RunResponse>(base2), api.get<{ reels: readonly ResearchRunReelView[] }>(`${base2}/reels?limit=${TOP_REELS_LIMIT}`)]);
        setActiveRun(run);
        setReels(rr.reels);
      } catch {
        setError("Could not load the Research Run.");
      }
    },
    [base],
  );

  const loadRuns = useCallback(
    async (pid: string) => {
      try {
        const r = await api.get<{ runs: readonly ResearchRunView[] }>(`${base}/personas/${encodeURIComponent(pid)}/runs`);
        setRuns(r.runs);
      } catch {
        setError("Could not load Research Runs.");
      }
    },
    [base],
  );

  const loadBank = useCallback(
    async (id: string) => {
      setBank(null);
      try {
        const r = await api.get<{ bank: { state: ResearchBankState } }>(`${base}/personas/${encodeURIComponent(id)}/bank`);
        setBank(r.bank.state);
      } catch {
        setError("Could not load the Research Bank.");
      }
    },
    [base],
  );

  const choosePersona = (id: string) => {
    setPersonaId(id);
    setMessage(null);
    setError(null);
    setBank(null);
    setRuns(null);
    setActiveRun(null);
    setReels(null);
    if (id !== "") {
      void loadBank(id);
      void loadRuns(id);
    }
  };

  const runError = (e: unknown, fallback: string): string => {
    const body = e instanceof ApiError ? (e.body as { error?: string; message?: string } | null) : null;
    return (body?.error !== undefined ? RUN_ERROR_MESSAGE[body.error] : undefined) ?? body?.message ?? fallback;
  };

  /** Performance Analysis: a SEPARATE request per bounded slice of the stored data (never part of a collection request); follows the cursor until the run is covered. */
  const analyzeAll = async (pid: string, runId: string) => {
    let cursor: string | null = null;
    let guard = 0;
    do {
      const url = `${base}/personas/${encodeURIComponent(pid)}/runs/${encodeURIComponent(runId)}/analyze${cursor === null ? "" : `?cursor=${encodeURIComponent(cursor)}`}`;
      const slice: { analyzed: number; nextCursor: string | null } = await api.post<{ analyzed: number; nextCursor: string | null }>(url, csrfToken);
      cursor = slice.nextCursor ?? null;
    } while (cursor !== null && mounted.current && guard++ < 1000);
  };

  /**
   * A run is collected in bounded steps (each request is short and the run is durable on the server). While the run reports `steps_remaining` this page
   * asks for the next step - still part of the user's own action; leaving the page just pauses the run (a Continue button resumes it).
   */
  const driveRun = async (first: RunResponse) => {
    let current = first;
    setActiveRun(current);
    let guard = 0;
    while (mounted.current && current.run.status === "partial" && current.run.statusDetail === "steps_remaining" && guard++ < current.run.creatorsTotal + 2) {
      current = await api.post<RunResponse>(`${base}/personas/${encodeURIComponent(personaId)}/runs/${encodeURIComponent(current.run.id)}/continue`, csrfToken);
      if (mounted.current) setActiveRun(current);
    }
    // Only after the collection requests have returned: the analysis of the stored data, as its own request(s). If it fails the Reels simply show
    // "not analyzed yet" and the Compute performance button recomputes it.
    if (mounted.current) {
      try {
        await analyzeAll(personaId, current.run.id);
      } catch {
        // shown as "not analyzed yet"
      }
    }
    if (mounted.current) await Promise.all([loadRun(personaId, current.run.id), loadRuns(personaId)]);
  };

  /** The ONLY way a run starts: this explicit click. The server does one bounded step per request; there is no scheduler and no background worker. */
  const startRun = async () => {
    setRunBusy(true);
    setMessage(null);
    setError(null);
    try {
      await driveRun(await api.post<RunResponse>(`${base}/personas/${encodeURIComponent(personaId)}/runs`, csrfToken));
    } catch (e) {
      if (mounted.current) setError(runError(e, "Could not start the Research Run."));
    } finally {
      if (mounted.current) setRunBusy(false);
    }
  };

  /** Resumes a run that still has creators that were never attempted (e.g. the page was closed mid-run). */
  const continueRun = async (runId: string) => {
    setRunBusy(true);
    setMessage(null);
    setError(null);
    try {
      await driveRun(await api.post<RunResponse>(`${base}/personas/${encodeURIComponent(personaId)}/runs/${encodeURIComponent(runId)}/continue`, csrfToken));
    } catch (e) {
      if (mounted.current) setError(runError(e, "Could not continue the Research Run."));
    } finally {
      if (mounted.current) setRunBusy(false);
    }
  };

  /** Recomputes the Performance Analysis from the data already stored (no provider call). */
  const analyzeRun = async (runId: string) => {
    setRunBusy(true);
    setMessage(null);
    setError(null);
    try {
      await analyzeAll(personaId, runId);
      await loadRun(personaId, runId);
    } catch (e) {
      if (mounted.current) setError(runError(e, "Could not run the performance analysis."));
    } finally {
      if (mounted.current) setRunBusy(false);
    }
  };

  const retryRun = async (runId: string) => {
    setRunBusy(true);
    setMessage(null);
    setError(null);
    try {
      await driveRun(await api.post<RunResponse>(`${base}/personas/${encodeURIComponent(personaId)}/runs/${encodeURIComponent(runId)}/retry`, csrfToken));
    } catch (e) {
      if (mounted.current) setError(runError(e, "Could not retry the Research Run."));
    } finally {
      if (mounted.current) setRunBusy(false);
    }
  };

  const setupBank = async () => {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      await api.post<{ bank: { state: ResearchBankState } }>(`${base}/personas/${encodeURIComponent(personaId)}/bank/setup`, csrfToken, {});
      setBank("ready");
      setMessage("Research Bank is ready.");
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { error?: string; message?: string } | null) : null;
      if (body?.error === "research_bank_needs_attention") setBank("needs_attention");
      setError((body?.error !== undefined ? BANK_ERROR_MESSAGE[body.error] : undefined) ?? "Could not set up the Research Bank.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reel-research">
      <p>
        <Link to="/generate/create">← Create</Link>
      </p>
      <h2>REEL — Research</h2>
      <p className="muted">Choose a persona to research Reels for. Research uses the Research Bank already connected to that persona.</p>

      <label>
        Persona{" "}
        <select data-testid="reel-persona-select" value={personaId} onChange={(e) => choosePersona(e.target.value)} disabled={personas === null}>
          <option value="">{personas === null ? "Loading…" : "Select a persona"}</option>
          {(personas ?? []).map((p) => (
            <option key={p.personaId} value={p.personaId}>
              {p.displayName}
            </option>
          ))}
        </select>
      </label>
      {personas !== null && personas.length === 0 ? <p className="muted">No personas available.</p> : null}

      {personaId !== "" && (bank === "not_set_up" || bank === "needs_attention") ? (
        <section data-testid="reel-bank-state">
          {bank === "not_set_up" ? (
            <div data-testid="reel-bank-setup">
              <p className="muted">Research is not set up for this persona yet.</p>
              {canAct ? (
                <button type="button" data-testid="reel-bank-setup-button" disabled={busy} onClick={() => void setupBank()}>
                  Set up Research Bank
                </button>
              ) : (
                <p className="muted">Your role cannot set up the Research Bank.</p>
              )}
            </div>
          ) : (
            <p data-testid="reel-bank-attention" role="alert">
              {BANK_ERROR_MESSAGE.research_bank_needs_attention}
            </p>
          )}
        </section>
      ) : null}

      {personaId !== "" ? (
        <section data-testid="reel-runs">
          <h3>Research Run</h3>
          <p className="muted">Finds the strongest recent Reels for this persona's Research Bank. It only runs when you start it.</p>
          {canAct ? (
            <button type="button" data-testid="reel-start-run" onClick={() => void startRun()} disabled={runBusy}>
              {runBusy ? "Collecting…" : "Start Research"}
            </button>
          ) : (
            <p className="muted">Your role cannot start a Research Run.</p>
          )}
          {runs !== null && runs.length === 0 ? <p className="muted">No runs yet.</p> : null}
          {runs !== null && runs.length > 0 ? (
            <ul data-testid="reel-run-list">
              {runs.map((r) => (
                <li key={r.id}>
                  <button type="button" onClick={() => void loadRun(personaId, r.id)}>
                    {r.startedAt.slice(0, 16).replace("T", " ")} — {r.status}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {activeRun !== null ? (
            <div data-testid="reel-run-detail">
              <h4>
                Run status: <span data-testid="reel-run-status">{activeRun.run.status}</span>
                {activeRun.run.statusDetail !== null ? ` (${activeRun.run.statusDetail})` : ""}
              </h4>
              <ul data-testid="reel-run-creators">
                {activeRun.creators.map((c) => (
                  <li key={c.creatorId} data-testid={`reel-run-creator-${c.canonicalAccountId}`}>
                    @{c.canonicalAccountId} — {c.status}
                    {c.status === "completed" || c.status === "partial"
                      ? ` · ${c.reelsReceived} reels received (${c.reelsNew} new, ${c.reelsSeenBefore} seen in earlier runs${c.duplicatesSkipped > 0 ? `, ${c.duplicatesSkipped} duplicates skipped` : ""}${c.rejectedCount > 0 ? `, ${c.rejectedCount} without an Instagram URL` : ""})${c.truncated ? " · list cut short" : ""}`
                      : ""}
                    {c.errorMessage !== null ? ` · ${c.errorMessage}` : ""}
                  </li>
                ))}
              </ul>
              {runBusy ? <p data-testid="reel-run-progress" className="muted">Collecting… {activeRun.creators.filter((c) => c.status !== "pending").length} of {activeRun.run.creatorsTotal} creators done</p> : null}
              {canAct && !runBusy && activeRun.run.statusDetail === "steps_remaining" ? (
                <button type="button" data-testid="reel-continue-run" onClick={() => void continueRun(activeRun.run.id)}>
                  Continue collecting
                </button>
              ) : null}
              {canAct && activeRun.run.status !== "completed" && activeRun.run.status !== "running" && activeRun.run.statusDetail !== "steps_remaining" ? (
                <button type="button" data-testid="reel-retry-run" onClick={() => void retryRun(activeRun.run.id)} disabled={runBusy}>
                  Retry incomplete creators
                </button>
              ) : null}
              {reels !== null && reels.length === 0 ? <p className="muted">No Reels collected in this run.</p> : null}
              {reels !== null && reels.length > 0 ? (
                <p data-testid="reel-analysis-summary" className="muted">
                  Relative Performance = views ÷ the creator's median views, highest first. Interesting = at least 3.0×.{" "}
                  {reels.filter((r) => r.analysis?.interesting === true).length} interesting of {reels.length} Reels.
                  {reels.some((r) => r.analysis === null) && canAct ? (
                    <>
                      {" "}
                      <button type="button" data-testid="reel-analyze-run" onClick={() => void analyzeRun(activeRun.run.id)} disabled={runBusy}>
                        Compute performance
                      </button>
                    </>
                  ) : null}
                </p>
              ) : null}
              {reels !== null && reels.length > 0 ? (
                <ReelUsScoreBar us={us} canAct={canAct} />
              ) : null}
              {reels !== null && reels.length > 0 ? (
                <ReelTop20 key={`${personaId}|${activeRun.run.id}`} base={base} personaId={personaId} runId={activeRun.run.id} reels={sortReelsByUsScore(reels, us.byReel)} usScores={us.byReel} csrfToken={csrfToken} canAct={canAct} onApproved={() => setApprovedRefresh((n) => n + 1)} onChanged={() => void loadRun(personaId, activeRun.run.id)} />
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}

      {personaId !== "" ? <ReelApproved base={base} personaId={personaId} csrfToken={csrfToken} canAct={canAct} refreshKey={approvedRefresh} /> : null}

      {message !== null ? <p data-testid="reel-message">{message}</p> : null}
      {error !== null ? <p role="alert" data-testid="reel-error">{error}</p> : null}
    </div>
  );
}
